<template>
	<div class="contentBlock paddingTop contract">
		<fragment
			v-for="(block, index) in processedHtml"
			:key="index"
		>
			<component
				:is="block.component"
				v-if="block.type === 'component'"
				v-bind="block.props"
			/>
			<template v-else>
				<!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
				<fragment v-html="block.content"></fragment>
			</template>
		</fragment>
	</div>
</template>

<script>
	import { Fragment } from 'vue-frag'
	import DOMPurify from 'dompurify'
	import CommissionRates from '../sections/CommissionRates.vue'
	import Expenses from '../sections/Expenses.vue'
	import Provenance from '../sections/Provenance.vue'
	import ReturnedFlatFee from '../sections/ReturnedFlatFee.vue'
	import ReturnedFlatPercentage from '../sections/ReturnedFlatPercentage.vue'

	export default {
		name: 'Content',
		components: {
			Fragment
		},
		props: ['contract'],
		data() {
			return {
				formData: {
					provenance: this.contract.provenance,
					expenses: this.contract.expenses,
					commission: this.contract.commission,
					returned_flat_fee: this.contract.returned_flat_fee,
					returned_percentage: this.contract.returned_percentage
				}
			}
		},
		computed: {
			/**
			 * Sanitized content
			 * @returns {string} The sanitized content
			 */
			sanitizedContent() {
				const encodedHtml = this.contract.content.replace(/<input:([\w_]+)>/g, '[[input:$1]]')
				const sanitized = DOMPurify.sanitize(encodedHtml)
				return sanitized.replace(/\[\[input:([\w_]+)\]\]/g, '<input:$1>')
			},
			/**
			 * Processed HTML
			 * @returns {Array} The processed HTML
			 */
			processedHtml() {
				const parsed = this.parseHtml(this.sanitizedContent)
				return parsed
			}
		},
		mounted() {
			/**
			 * Fix contract sections
			 */
			this.$nextTick(() => {
				this.fixContractSections()
			})
		},
		methods: {
			/**
			 * Fix contract sections
			 */
			fixContractSections() {
				const contract = document.querySelector('.contract')
				if (!contract) return

				let lastSection = null
				let lastBlock = null
				const children = Array.from(contract.children)
				children.forEach((child) => {
					if (child.nodeType === 1 && child.classList.contains('contractSection')) {
						// Found a new contractSection, update the last known section
						lastSection = child
						const blocks = lastSection.querySelectorAll('.contractBlock')
						lastBlock = blocks.length > 0 ? blocks[blocks.length - 1] : null
					} else if (lastBlock) {
						// Move elements and non-empty text nodes into the last contractBlock
						if (child.nodeType === 1 || (child.nodeType === 3 && child.textContent.trim() !== '')) {
							lastBlock.appendChild(child)
						}
					}
				})
			},
			/**
			 * Parse HTML
			 * @param {string} htmlString - The HTML string
			 * @returns {Array} The parsed HTML
			 */
			parseHtml(htmlString) {
				const parts = htmlString.split(/(<input:[\w_]+>)/g)

				const components = {
					provenance: Provenance,
					expenses: Expenses,
					commission: CommissionRates,
					returned_flat_fee: ReturnedFlatFee,
					returned_percentage: ReturnedFlatPercentage
				}

				const matchedComponents = []

				return parts.flatMap((chunk, index) => {
					const match = chunk.match(/<input:([\w_]+)>/)
					if (match) {
						const fieldName = match[1]
						matchedComponents.push({
							type: 'component',
							component: components[fieldName],
							props: { contract: this.contract },
							key: fieldName
						})
						return [
							{
								type: 'component',
								component: components[fieldName],
								props: { contract: this.contract },
								key: fieldName
							}
						]
					}

					let trimmedText = chunk.replace(/\s+/g, ' ').trim()

					// If we have matched all the components, we need to split the trimmed text at the first instance of a <div class="contractSection"
					if (matchedComponents.length === Object.keys(components).length) {
						const sectionMatch = trimmedText.match(/<\/div> <\/div>/)
						if (sectionMatch) {
							const nextIndex = sectionMatch.index + sectionMatch[0].length
							const precedingText = trimmedText.slice(0, nextIndex)
							const followingText = trimmedText.slice(nextIndex)
							return [
								{ type: 'html', content: `<span>${precedingText}</span>` },
								{ type: 'html', content: followingText }
							]
						}
					}

					if (trimmedText) {
						if (!trimmedText.match(/^<\/?[a-z][\s\S]*>/i) && index !== 0) {
							// If the chunk doesn't start with an HTML tag, wrap it in a <span>
							return [{ type: 'html', content: `<span>${trimmedText}</span>` }]
						} else {
							// If it starts with an HTML tag but has leading text, split it
							const splitMatch = trimmedText.match(/^([^<]+)(<.*)$/s)
							if (splitMatch) {
								return [
									{
										type: 'html',
										content: `<span>${splitMatch[1].trim()}</span>`
									},
									{ type: 'html', content: splitMatch[2] }
								]
							}
						}
					}

					return [{ type: 'html', content: trimmedText }]
				})
			}
		}
	}
</script>

<style scoped></style>
